import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { CustomTooltip, Spinner } from '@cian/ui-kit';
import { clsx } from 'clsx';
import * as React from 'react';
import * as styles from './CianChatButtonWithAnimation.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var CianChatButtonWithAnimation = function CianChatButtonWithAnimation(_ref) {
  var onClick = _ref.onClick,
    isLoading = _ref.isLoading,
    isButtonAnimationVisible = _ref.isButtonAnimationVisible,
    isTooltipAnimationVisible = _ref.isTooltipAnimationVisible,
    _ref$animationDelayMs = _ref.animationDelayMs,
    animationDelayMs = _ref$animationDelayMs === void 0 ? 0 : _ref$animationDelayMs,
    onAnimationStarted = _ref.onAnimationStarted;
  var anchorRef = React.useRef(null);
  var videoRef = React.useRef(null);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isAnimationStarted = _React$useState2[0],
    setIsAnimationStarted = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isTooltipAnimationStarted = _React$useState4[0],
    setIsTooltipAnimationStarted = _React$useState4[1];

  // Start/stop animation with optional delay, pausing countdown when tab is hidden
  React.useEffect(function () {
    var timeoutId;
    var startTime = 0;
    var remaining = animationDelayMs;
    var clearTimer = function clearTimer() {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    var onTimeout = function onTimeout() {
      onAnimationStarted === null || onAnimationStarted === void 0 || onAnimationStarted();
      setIsAnimationStarted(true);
      clearTimer();
    };
    var startTimer = function startTimer() {
      if (remaining <= 0) {
        onTimeout();
        return;
      }
      startTime = performance.now();
      timeoutId = window.setTimeout(onTimeout, remaining);
    };
    var handleVisibilityChange = function handleVisibilityChange() {
      if (!isButtonAnimationVisible || animationDelayMs === 0) {
        return;
      }
      if (document.visibilityState === 'hidden') {
        // pause
        if (timeoutId) {
          var elapsed = performance.now() - startTime;
          remaining = Math.max(0, remaining - elapsed);
          clearTimer();
        }
      } else {
        // resume
        if (!timeoutId) {
          startTimer();
        }
      }
    };
    if (isButtonAnimationVisible) {
      if (animationDelayMs > 0) {
        startTimer();
        document.addEventListener('visibilitychange', handleVisibilityChange);
      } else {
        setIsAnimationStarted(true);
      }
    } else {
      setIsAnimationStarted(false);
    }
    return function () {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isButtonAnimationVisible, animationDelayMs]);

  // Tooltip animation with optional delay (reuses animationDelayMs), pausing when tab is hidden
  React.useEffect(function () {
    var timeoutId;
    var startTime = 0;
    var remaining = animationDelayMs;
    var clearTimer = function clearTimer() {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    var onTimeout = function onTimeout() {
      setIsTooltipAnimationStarted(true);
      clearTimer();
    };
    var startTimer = function startTimer() {
      if (remaining <= 0) {
        onTimeout();
        return;
      }
      startTime = performance.now();
      timeoutId = window.setTimeout(onTimeout, remaining);
    };
    var handleVisibilityChange = function handleVisibilityChange() {
      if (!isTooltipAnimationVisible || animationDelayMs === 0) {
        return;
      }
      if (document.visibilityState === 'hidden') {
        if (timeoutId) {
          var elapsed = performance.now() - startTime;
          remaining = Math.max(0, remaining - elapsed);
          clearTimer();
        }
      } else {
        if (!timeoutId) {
          startTimer();
        }
      }
    };
    if (isTooltipAnimationVisible) {
      if (animationDelayMs > 0) {
        startTimer();
        document.addEventListener('visibilitychange', handleVisibilityChange);
      } else {
        setIsTooltipAnimationStarted(true);
      }
    } else {
      setIsTooltipAnimationStarted(false);
    }
    return function () {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTooltipAnimationVisible, animationDelayMs]);

  // Video playback with optional delay, pausing countdown when tab is hidden
  React.useEffect(function () {
    var timeoutId;
    var startTime = 0;
    var remaining = animationDelayMs;
    var clearTimer = function clearTimer() {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    var playVideo = /*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _videoRef$current;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 3;
              return (_videoRef$current = videoRef.current) === null || _videoRef$current === void 0 ? void 0 : _videoRef$current.play();
            case 3:
              _context.next = 7;
              break;
            case 5:
              _context.prev = 5;
              _context.t0 = _context["catch"](0);
            case 7:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[0, 5]]);
      }));
      return function playVideo() {
        return _ref2.apply(this, arguments);
      };
    }();
    var onTimeout = function onTimeout() {
      playVideo();
      clearTimer();
    };
    var startTimer = function startTimer() {
      if (remaining <= 0) {
        onTimeout();
        return;
      }
      startTime = performance.now();
      timeoutId = window.setTimeout(onTimeout, remaining);
    };
    var handleVisibilityChange = function handleVisibilityChange() {
      if (!isButtonAnimationVisible || animationDelayMs === 0) {
        return;
      }
      if (document.visibilityState === 'hidden') {
        if (timeoutId) {
          var elapsed = performance.now() - startTime;
          remaining = Math.max(0, remaining - elapsed);
          clearTimer();
        }
      } else {
        if (!timeoutId) {
          startTimer();
        }
      }
    };
    if (isButtonAnimationVisible) {
      if (animationDelayMs > 0) {
        startTimer();
        document.addEventListener('visibilitychange', handleVisibilityChange);
      } else {
        onTimeout();
      }
    } else {
      // Pause and reset video if animation is hidden
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
    return function () {
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isButtonAnimationVisible, animationDelayMs]);
  return /*#__PURE__*/_jsxs("div", {
    className: styles['container'],
    children: [/*#__PURE__*/_jsx("div", {
      className: clsx(styles['animation-container'], isAnimationStarted && styles['animation']),
      children: /*#__PURE__*/_jsx("div", {
        className: styles['video-container'],
        children: /*#__PURE__*/_jsx("video", {
          ref: videoRef,
          className: styles['video'],
          autoPlay: false,
          muted: true,
          playsInline: true,
          preload: "none",
          children: /*#__PURE__*/_jsx("source", {
            src: "https://static.cdn-cian.ru/frontend/collections-assets/cian-kpn-chat/animation.mp4",
            type: "video/mp4"
          })
        })
      })
    }), /*#__PURE__*/_jsx("button", {
      ref: anchorRef,
      "data-testid": "consultantCianChatButton",
      className: styles['button'],
      onClick: onClick,
      children: isLoading ? /*#__PURE__*/_jsx("div", {
        className: styles['loader'],
        children: /*#__PURE__*/_jsx(Spinner, {
          size: 20,
          color: "white_100"
        })
      }) : /*#__PURE__*/_jsx("div", {
        className: styles['content'],
        children: "\u041D\u0430\u043F\u0438\u0441\u0430\u0442\u044C \u043A\u043E\u043D\u0441\u0443\u043B\u044C\u0442\u0430\u043D\u0442\u0443"
      })
    }), /*#__PURE__*/_jsx("div", {
      className: clsx(styles['tooltip'], isTooltipAnimationStarted && styles['tooltip-animation']),
      children: /*#__PURE__*/_jsx(CustomTooltip, {
        open: true,
        portal: false,
        anchorRef: anchorRef,
        content: "\u0417\u0430\u0434\u0430\u0439\u0442\u0435 \u0432\u043E\u043F\u0440\u043E\u0441 \u043A\u043E\u043D\u0441\u0443\u043B\u044C\u0442\u0430\u043D\u0442\u0443 \u043F\u043E\xA0\u043D\u043E\u0432\u043E\u0441\u0442\u0440\u043E\u0439\u043A\u0430\u043C",
        placement: "top-end",
        theme: "blue",
        arrow: true,
        maxWidth: 139
      })
    })]
  });
};